if (!customElements.get('dl-pop-raffle-y')) {
  class DLPopRaffleY extends HTMLElement {
    constructor() {
      super();
      this.phase = 'idle';
      this.tick = 0;
      this.winner = null;
      this.drawRequest = 0;
      this.timers = [];
      this.raf = 0;
      this.idleAnimations = [];
      this.idleAnimationStartedAt = 0;
      this.idleAnimationStartTick = 0;
      this.onResize = this.fit.bind(this);
      this.onShowRequest = this.handleShowRequest.bind(this);
      this.onHideRequest = this.handleHideRequest.bind(this);
      this.onTriggerClick = this.handleTriggerClick.bind(this);
    }

    connectedCallback() {
      this.cards = [...this.querySelectorAll('[data-raffle-card]')];
      if (!this.cards.length) return;
      this.cardFlippers = this.cards.map((card) => card.querySelector('.dl-raffle-y__card-flipper'));
      this.can_not_be_replaced = this.dataset.dlCanNotBeReplaced === 'true';
      this.exclusive_while_showing = this.dataset.dlExclusiveWhileShowing === 'true';
      this.event_pop_id = this.dataset.dlEventPopupId;
      this.showing = false;
      this.overlay = this.querySelector('.dl-raffle-y-overlay');
      this.overlayInner = this.querySelector('.dl-raffle-y-overlay-inner');
      this.bindEvents();
      this.registerSharedMini();
      this.fit();
      this.setPhase('idle');
      this.init();
    }

    disconnectedCallback() { this.cleanup(); }

    init() {
      this._initializePopup();
    }

    _initializePopup() {
      this.condition_page_view();
      this.miniScroll();

      console.log('[DLPopRaffleY ready]', {
        section_id: this.dataset.sectionId,
        timing_mode: this.dataset.dlTriggerTimingMode || 'delay_only',
        delay: this._getTriggerDelay(),
        scroll_depth: this._getTriggerScrollDepth(),
        time: new Date().toISOString(),
        time_ms: Date.now()
      });
    }

    condition_page_view() {
      this._conditionPageViewLogic();
    }

    getStorageKey(type) {
      return `dl-pop-h-${type}-${this.id}`;
    }

    getMiniSourceId() {
      return this.dataset.sectionId || this.id;
    }

    getShopifyStore() {
      let store = localStorage.getItem('braze_lastest_shopify_store');
      if (!store) {
        let subdomain = window.location.hostname.split('.')[0];
        subdomain = subdomain === 'petlibro' || subdomain === 'ca' ? 'us' : subdomain;
        localStorage.setItem('braze_lastest_shopify_store', subdomain);
        store = subdomain;
      }
      return store;
    }

    capturePixel(eventName, extra = {}) {
      const eventData = {
        pop_id: this.event_pop_id,
        currentURLInfo: window.DLPixelDataHelper?.URLHelper?.getCurrentPageInfo?.() ?? null,
        pop_type: this.dataset.dlType || 'raffle',
        ...extra
      };
      // 只上报 Amplitude：Braze 订阅/用户合并由后端 draw 接口处理，避免前端 SDK 双写。
      const pixelHub = window.DLPixelH;
      if (typeof pixelHub?.getAllInstances === 'function') {
        const amplitude = pixelHub.getAllInstances().find((instance) => {
          const instanceName = instance?.name || instance?.constructor?.name;
          return instanceName === 'AmplitudePixel' || instanceName === 'DLAmplitudePixel';
        });
        if (amplitude && typeof amplitude.capture === 'function') {
          amplitude.capture(eventName, eventData);
          return;
        }
      }
      if (typeof Shopify !== 'undefined' && Shopify.analytics?.publish) {
        Shopify.analytics.publish(`[Petlibro] ${eventName}`, eventData);
      }
    }

    getCloseInputProps() {
      let close_stage = 'email input';
      let input = null;

      if (this.phase === 'check') {
        close_stage = 'finished';
        input = this.querySelector('[data-phone-form] input[name="phone"]');
      } else if (['done', 'more'].includes(this.phase)) {
        close_stage = 'phone input';
        input = this.querySelector('[data-phone-form] input[name="phone"]');
      } else if (this.phase === 'idle') {
        close_stage = 'email input';
        input = this.querySelector('.dl-raffle-y__form input[name="email"]');
      } else {
        // gather / flip / shuffle / pull / draw / reveal → flip
        close_stage = 'flip';
      }

      return {
        close_stage,
        has_input_content: Boolean(String(input?.value || '').trim())
      };
    }

    closeByUser(showMini = true) {
      const wasShowing = this.showing;
      this.capturePixel('pop_up_close', this.getCloseInputProps());
      this.close();
      if (wasShowing && showMini) window.DLPopMiniBar?.showExpanded?.(this.getMiniSourceId());
    }

    check24HourLimit() {
      const lastShowTime = Number(localStorage.getItem(this.getStorageKey('24h')) || 0);
      return !lastShowTime || Date.now() - lastShowTime >= 24 * 60 * 60 * 1000;
    }

    check7DayLimit() {
      let history = [];
      try {
        history = JSON.parse(localStorage.getItem(this.getStorageKey('7d')) || '[]');
      } catch (_) {
        history = [];
      }
      if (!Array.isArray(history)) history = [];
      history = history.filter((time) => Date.now() - Number(time) < 7 * 24 * 60 * 60 * 1000);
      localStorage.setItem(this.getStorageKey('7d'), JSON.stringify(history));
      return history.length < 3;
    }

    checkSessionLimit() {
      return Number(sessionStorage.getItem(this.getStorageKey('session')) || 0) < 2;
    }

    getCanShow(ignoreLimit = false) {
      if (!ignoreLimit && !this.check24HourLimit()) return false;
      if (!ignoreLimit && !this.check7DayLimit()) return false;
      if (!ignoreLimit && !this.checkSessionLimit()) return false;
      return true;
    }

    check_show_page() {
      const isNotOnPages = this.dataset.dlIsNotOnPage === 'true';
      const pages = (this.dataset.dlNotOnPage || '').split('\n');
      let matched = false;

      pages.forEach((page) => {
        if (!page.trim()) return;
        try {
          const targetPath = new URL(page, location.origin).pathname;
          const currentPath = location.pathname;
          if (targetPath === '/' && currentPath === '/') matched = true;
          if (targetPath !== '/' && (currentPath === targetPath || currentPath.startsWith(`${targetPath}/`))) matched = true;
        } catch (error) {
          console.warn('[DLPopRaffleY] Invalid page URL', page, error);
        }
      });

      return isNotOnPages ? matched : !matched;
    }

    check_only_unsubscribed() {
      if (this.dataset.dlTriggerUnsubscribed !== 'true') return true;
      if (this.dataset.dlCustomer !== 'null') return false;
      return localStorage.getItem('dl-pop-h-finished') === null;
    }

    getDrawn() {
      try {
        const drawn = JSON.parse(localStorage.getItem(this.getStorageKey('drawn')) || 'null');
        if (!drawn || typeof drawn !== 'object') return null;
        return drawn;
      } catch (_) {
        return null;
      }
    }

    hasDrawn() {
      return Boolean(this.getDrawn());
    }

    hasFinished() {
      return Boolean(localStorage.getItem(this.getStorageKey('finished')));
    }

    markDrawn(prizeId, winner, email) {
      localStorage.setItem(this.getStorageKey('drawn'), JSON.stringify({
        prizeId,
        winner,
        revealed: false,
        email: String(email || this.submittedEmail || '').trim(),
        at: Date.now()
      }));
    }

    markRevealed() {
      const drawn = this.getDrawn();
      if (!drawn) return;
      localStorage.setItem(this.getStorageKey('drawn'), JSON.stringify({
        ...drawn,
        revealed: true
      }));
    }

    restoreDrawnPhase(immediate = false) {
      if (this.hasFinished()) return;
      const drawn = this.getDrawn();
      if (!drawn) return;
      const winner = this.cards.findIndex((card) => card.dataset.prizeId === String(drawn.prizeId ?? ''));
      this.winner = winner >= 0 ? winner : Number(drawn.winner);
      if (!Number.isInteger(this.winner) || this.winner < 0) return;
      this.submittedEmail = String(drawn.email || '').trim();
      if (immediate) this.classList.add('is-restoring');
      // 兼容旧数据：旧记录没有 revealed 字段，仍按已经翻牌处理。
      this.setPhase(drawn.revealed === false ? 'draw' : 'more');
      if (immediate) {
        requestAnimationFrame(() => requestAnimationFrame(() => this.classList.remove('is-restoring')));
      }
    }

    checkBlockedPage() {
      return ['/pages/help-center', '/pages/faq-video', '/pages/faq-product', '/pages/ar-viewer', '/blogs/', '/policies/', '/tools/']
        .some((path) => location.href.includes(path));
    }

    recordShow() {
      const now = Date.now();
      localStorage.setItem(this.getStorageKey('opened'), String(now));
      localStorage.setItem(this.getStorageKey('24h'), String(now));

      let history = [];
      try {
        history = JSON.parse(localStorage.getItem(this.getStorageKey('7d')) || '[]');
      } catch (_) {
        history = [];
      }
      if (!Array.isArray(history)) history = [];
      history.push(now);
      localStorage.setItem(this.getStorageKey('7d'), JSON.stringify(history));

      const count = Number(sessionStorage.getItem(this.getStorageKey('session')) || 0);
      sessionStorage.setItem(this.getStorageKey('session'), String(count + 1));
    }

    lockBodyScroll() {
      const body = document.body;
      const html = document.documentElement;
      if (body.dataset.dlPopHScrollLocked === 'true') return;
      const scrollTop = window.scrollY || window.pageYOffset || html.scrollTop || 0;
      body.dataset.dlPopHScrollLocked = 'true';
      body.dataset.dlPopHScrollTop = String(scrollTop);
      html.classList.add('dl-pop-h-html-locked');
      // Desktop Safari: body position:fixed + a fullscreen overlay can swallow
      // all window hits (including Shopify's preview bar). iOS still needs it.
      const isIOS = /iP(hone|ad|od)/.test(navigator.userAgent)
        || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
      if (isIOS) {
        body.classList.add('dl-pop-h-body-locked');
        body.style.top = `-${scrollTop}px`;
      }
    }

    unlockBodyScroll() {
      const body = document.body;
      const html = document.documentElement;
      if (body.dataset.dlPopHScrollLocked !== 'true') return;
      if (document.querySelector('.dl-pop-h-overlay.dl-show, dl-pop-raffle-y[data-showing="true"]')) return;
      const scrollTop = Number(body.dataset.dlPopHScrollTop || 0);
      const originalScrollBehavior = html.style.scrollBehavior;
      html.style.scrollBehavior = 'auto';
      body.classList.remove('dl-pop-h-body-locked');
      html.classList.remove('dl-pop-h-html-locked');
      body.style.top = '';
      delete body.dataset.dlPopHScrollLocked;
      delete body.dataset.dlPopHScrollTop;
      window.scrollTo({ top: scrollTop, left: 0, behavior: 'auto' });
      if (originalScrollBehavior) html.style.scrollBehavior = originalScrollBehavior;
      else html.style.removeProperty('scroll-behavior');
    }

    // 与 dl-pop-h 保持一致：支持等待时长、滑动深度、最先达成三种模式。
    _conditionPageViewLogic() {
      const mode = this.dataset.dlTriggerTimingMode || 'delay_only';
      const delay = this._getTriggerDelay();
      const scrollDepth = this._getTriggerScrollDepth();

      if (mode === 'delay_only') {
        this._triggerByDelay(delay);
        return;
      }

      if (mode === 'scroll_only') {
        this._triggerByScrollDepth(scrollDepth);
        return;
      }

      this._triggerByWhicheverFirst(delay, scrollDepth);
    }

    // 与 dl-pop-h 相同，≤990 为手机端，>990 为 PC 端。
    _isMobileViewport() {
      return window.innerWidth <= 990;
    }

    _getTriggerDelay() {
      const raw = this._isMobileViewport()
        ? this.dataset.dlTriggerDelayM
        : this.dataset.dlTriggerDelayPc;
      const delay = Number(raw);
      return Number.isFinite(delay) ? delay : 0;
    }

    _getTriggerScrollDepth() {
      const raw = this._isMobileViewport()
        ? this.dataset.dlTriggerScrollDepthM
        : this.dataset.dlTriggerScrollDepthPc;
      const depth = Number(raw);
      return Number.isFinite(depth) ? depth : 0;
    }

    _triggerByDelay(delay) {
      this._pageViewDelayTimer = setTimeout(() => {
        this.triggerType = 'time_delay';
        this.show_by_frequency();
      }, delay * 1000);
    }

    // 当前滑动深度 = 视口垂直中线位置 / 文档总高 * 100。
    _getScrollDepthPercent() {
      if (this._isScrollDepthUnreliable()) return 0;

      const scrollTop = window.pageYOffset || document.documentElement.scrollTop || 0;
      const winHeight = window.innerHeight || document.documentElement.clientHeight || 0;
      const docHeight = document.documentElement.scrollHeight || 0;
      if (docHeight <= 0) return 0;
      const midLine = scrollTop + winHeight / 2;
      return Math.min((midLine / docHeight) * 100, 100);
    }

    _isScrollDepthUnreliable() {
      return document.body?.classList.contains('dl-raffle-y-open')
        || Boolean(document.querySelector('.dl-pop-h-overlay.dl-show'));
    }

    _triggerByScrollDepth(scrollDepth) {
      const tryTrigger = () => {
        if (this._pageViewTriggered || this._isScrollDepthUnreliable()) return;
        if (this._getScrollDepthPercent() >= scrollDepth) {
          this.triggerType = 'scroll_depth';
          this.show_by_frequency();
        }
      };

      tryTrigger();
      if (this._pageViewTriggered) return;
      this._pageViewScrollHandler = () => tryTrigger();
      window.addEventListener('scroll', this._pageViewScrollHandler, { passive: true });
    }

    _triggerByWhicheverFirst(delay, scrollDepth) {
      const triggerOnce = (type) => {
        if (this._pageViewTriggered) return;
        if (type === 'scroll_depth' && this._isScrollDepthUnreliable()) return;

        this.triggerType = type;
        this.show_by_frequency();
      };

      this._pageViewDelayTimer = setTimeout(() => triggerOnce('time_delay'), delay * 1000);
      const tryScroll = () => {
        if (this._isScrollDepthUnreliable()) return;
        if (this._getScrollDepthPercent() >= scrollDepth) triggerOnce('scroll_depth');
      };

      tryScroll();
      if (this._pageViewTriggered) return;
      this._pageViewScrollHandler = () => tryScroll();
      window.addEventListener('scroll', this._pageViewScrollHandler, { passive: true });
    }

    _cleanupPageViewScrollListener() {
      if (!this._pageViewScrollHandler) return;
      window.removeEventListener('scroll', this._pageViewScrollHandler);
      this._pageViewScrollHandler = null;
    }

    overlayClick(event) {
      if (event.target !== this.overlayInner) return;
      this.closeByUser();
    }

    bindEvents() {
      this.overlayInner?.addEventListener('click', this.overlayClick.bind(this));
      this.querySelectorAll('[data-raffle-close]').forEach((button) => button.addEventListener('click', () => this.closeByUser()));
      this.querySelector('.dl-raffle-y__form')?.addEventListener('submit', (event) => this.start(event));
      this.querySelector('[data-raffle-start]')?.addEventListener('click', (event) => this.start(event));
      this.querySelector('[data-phone-form]')?.addEventListener('submit', (event) => this.submitPhone(event));
      this.cards.forEach((card) => card.addEventListener('click', () => {
        if (this.phase === 'draw' && Number(card.dataset.index) === this.winner) {
          this.capturePixel('pop_up_flip', {
            prize_id: card.dataset.prizeId,
            card_index: Number(card.dataset.index)
          });
          this.reveal();
        }
      }));
      window.addEventListener('resize', this.onResize);
      this.addEventListener('keydown', (event) => { if (event.key === 'Escape') this.closeByUser(); });
      document.addEventListener('raffle:show', this.onShowRequest);
      document.addEventListener('raffle:hide', this.onHideRequest);
      document.addEventListener('click', this.onTriggerClick);
      this.querySelector('[data-raffle-finish]')?.addEventListener('click', () => this.finishAndClose());
    }

    registerSharedMini() {
      const content = (this.dataset.dlMiniContent || '').trim();
      const carouselContent = (this.dataset.dlMiniCarouselContent || '').trim();
      if ((!content && !carouselContent) || !window.DLPopMiniBar?.register) return;

      window.DLPopMiniBar.register({
        id: this.getMiniSourceId(),
        getContent: () => this.dataset.dlMiniContent || '',
        getContents: () => (this.dataset.dlMiniCarouselContent || '')
          .split(',')
          .map((item) => item.trim())
          .filter(Boolean),
        getOpenedAt: () => Number(localStorage.getItem(this.getStorageKey('opened')) || 0),
        canShow: () => this.canShowMini(),
        onOpen: () => {
          this.triggerType = 'click_mini';
          this.open(true);
        }
      });
    }

    matchesRequest(event) {
      const sectionId = event.detail?.sectionId;
      return !sectionId || sectionId === this.dataset.sectionId;
    }

    handleShowRequest(event) {
      if (this.matchesRequest(event)) this.open(true);
    }

    handleHideRequest(event) {
      if (this.matchesRequest(event)) this.close();
    }

    handleTriggerClick(event) {
      const trigger = event.target.closest?.('[data-raffle-trigger]');
      if (!trigger) return;
      const sectionId = trigger.dataset.raffleTrigger;
      if (!sectionId || sectionId === this.dataset.sectionId) {
        event.preventDefault();
        this.open(true);
      }
    }

    show_by_frequency() {
      return this._showPopup(false);
    }

    open(focus = false) {
      return this._showPopup(focus);
    }

    _getShowBlockReason(focus = false) {
      if (window.__DL_POPH_INTENT_LEAVE_TERMINAL_ID__ && !window.Shopify?.designMode) return 'intent_leave_terminal';
      if (focus) return '';
      if (window.cartUpsellPopupShowing) return 'cart_popup_showing';
      if (this.checkBlockedPage()) return 'blocked_page';
      if (this.check_show_page()) return 'page_rule';
      if (!this.hasDrawn() && !this.check_only_unsubscribed()) return 'subscribed_or_finished';
      if (this.hasFinished()) return 'raffle_finished';
      if (!focus && this.hasDrawn()) return 'raffle_drawn';
      if (!this.check24HourLimit()) return '24_hour_limit';
      if (!this.check7DayLimit()) return '7_day_limit';
      if (!this.checkSessionLimit()) return 'session_limit';

      const popHs = [...document.querySelectorAll('dl-pop-h')];
      const raffles = [...document.querySelectorAll('dl-pop-raffle-y')];
      if (popHs.some((pop) => pop.showing && pop.can_not_be_replaced)) return 'non_replaceable_popup_showing';
      if (popHs.some((pop) => pop.showing && pop.exclusive_while_showing)) return 'exclusive_popup_showing';
      if (raffles.some((pop) => pop !== this && pop.showing && pop.can_not_be_replaced)) return 'non_replaceable_raffle_showing';
      if (raffles.some((pop) => pop !== this && pop.showing && pop.exclusive_while_showing)) return 'exclusive_raffle_showing';
      return '';
    }

    _reportShowBlocked(reason) {
      if (this._lastShowBlockReason === reason) return;
      this._lastShowBlockReason = reason;
      console.info('[DLPopRaffleY] Trigger reached but popup was blocked', {
        reason,
        popup_id: this.event_pop_id,
        trigger_type: this.triggerType || 'unknown',
        scroll_depth: Number(this._getScrollDepthPercent().toFixed(2)),
        required_scroll_depth: this._getTriggerScrollDepth()
      });
    }

    _showPopup(focus = false) {
      const blockReason = this._getShowBlockReason(focus);
      if (blockReason) {
        this._reportShowBlocked(blockReason);
        return false;
      }
      this._lastShowBlockReason = '';

      this.cancelScheduledReset();
      // Reopen may cancel a deferred close-reset; force a clean phase first.
      this.reset();

      if (this._pageViewDelayTimer) {
        clearTimeout(this._pageViewDelayTimer);
        this._pageViewDelayTimer = null;
      }
      this._cleanupPageViewScrollListener();
      this._pageViewTriggered = true;

      const popHs = [...document.querySelectorAll('dl-pop-h')];
      const raffles = [...document.querySelectorAll('dl-pop-raffle-y')];

      popHs.forEach((pop) => {
        if (!pop.showing) return;
        if (typeof pop.be_replaced === 'function') pop.be_replaced();
        else pop.hide?.();
      });
      raffles.forEach((pop) => {
        if (pop !== this && pop.showing) pop.be_replaced?.();
      });

      void this.overlay?.offsetWidth;
      this.overlay?.classList.remove('dl-leaving');
      this.overlay?.classList.add('dl-show');
      this.dataset.showing = 'true';
      this.showing = true;
      document.body.classList.add('dl-raffle-y-open');
      this.lockBodyScroll();
      this.hideMini();
      localStorage.removeItem(this.getStorageKey('replaced'));
      if (!focus) this.recordShow();
      this.replace_has_showed();
      this.restoreDrawnPhase(true);
      if (this.phase === 'idle') this.animateIdle();
      this.capturePixel('pop_up_show', {
        pop_trigger_type: this.triggerType || 'manual'
      });
      this.dispatchEvent(new CustomEvent('raffle:open', { bubbles: true, detail: { triggerType: this.triggerType || 'manual' } }));
      return true;
    }

    close() {
      const wasShowing = this.showing;
      this.overlay?.classList.add('dl-leaving');
      this.overlay?.classList.remove('dl-show');
      this.dataset.showing = 'false';
      this.showing = false;
      document.body.classList.remove('dl-raffle-y-open');
      this.unlockBodyScroll();
      // Freeze current phase visuals during fade-out; reset after overlay hides
      // so reveal/more content doesn't flash back to entry mid-close.
      this.freezePhaseForClose();
      this.scheduleResetAfterClose();
      if (wasShowing && this.exclusive_while_showing) this.resetOtherTriggerTimers();
      this.dispatchEvent(new CustomEvent('raffle:close', { bubbles: true }));
    }

    freezePhaseForClose() {
      this.drawRequest += 1;
      this.timers.forEach(clearTimeout);
      this.timers = [];
      cancelAnimationFrame(this.raf);
      this.stopCompositorIdleAnimation();
    }

    scheduleResetAfterClose() {
      clearTimeout(this._resetAfterCloseTimer);
      this.teardownCloseResetListener();
      const overlay = this.overlay;
      const CLOSE_RESET_MS = 550;

      const runReset = () => {
        this._resetAfterCloseTimer = null;
        this.teardownCloseResetListener();
        if (this.showing) return;
        this.overlay?.classList.remove('dl-leaving');
        this.reset();
      };

      this._onCloseResetTransitionEnd = (event) => {
        if (event.target !== overlay) return;
        if (event.propertyName !== 'opacity' && event.propertyName !== 'visibility') return;
        runReset();
      };

      if (overlay) overlay.addEventListener('transitionend', this._onCloseResetTransitionEnd);
      this._resetAfterCloseTimer = setTimeout(runReset, CLOSE_RESET_MS);
    }

    teardownCloseResetListener() {
      if (!this._onCloseResetTransitionEnd) return;
      this.overlay?.removeEventListener('transitionend', this._onCloseResetTransitionEnd);
      this._onCloseResetTransitionEnd = null;
    }

    cancelScheduledReset() {
      clearTimeout(this._resetAfterCloseTimer);
      this._resetAfterCloseTimer = null;
      this.teardownCloseResetListener();
    }

    finish() {
      const now = Date.now();
      localStorage.setItem(this.getStorageKey('finished'), String(now));
      let finished = [];
      try {
        finished = JSON.parse(localStorage.getItem('dl-pop-h-finished') || '[]');
      } catch (_) {
        finished = [];
      }
      if (!Array.isArray(finished)) finished = [];
      if (!finished.includes(this.id)) finished.push(this.id);
      localStorage.setItem('dl-pop-h-finished', JSON.stringify(finished));
      this.hideMini();
    }

    finishAndClose() {
      this.finish();
      this.closeByUser(false);
    }

    be_replaced() {
      localStorage.setItem(this.getStorageKey('replaced'), String(Date.now()));
      this.close();
      this.hideMini();
    }

    replace_has_showed() {
      document.querySelectorAll('dl-pop-h').forEach((pop) => {
        if (pop.showing || pop.can_not_be_replaced) return;
        if (!localStorage.getItem(`dl-pop-h-opened-${pop.id}`)) return;
        if (pop.main?.dataset?.dlTriggerType === 'step') return;
        if (typeof pop._isIntentLeaveConditionPop === 'function' && pop._isIntentLeaveConditionPop()) return;
        if (typeof pop.check_show_page === 'function' && pop.check_show_page()) return;
        pop.be_replaced?.();
      });

      document.querySelectorAll('dl-pop-raffle-y').forEach((pop) => {
        if (pop === this || pop.showing || pop.can_not_be_replaced) return;
        if (!localStorage.getItem(pop.getStorageKey?.('opened'))) return;
        pop.be_replaced?.();
      });
    }

    resetOtherTriggerTimers() {
      setTimeout(() => {
        const hasMainPopup = document.querySelector('.dl-pop-h-overlay.dl-show, dl-pop-raffle-y[data-showing="true"]');
        if (hasMainPopup) return;
        document.querySelectorAll('dl-pop-h, dl-pop-raffle-y').forEach((pop) => {
          if (pop !== this) pop._resetTriggerTimers?.();
        });
      }, 0);
    }

    _resetTriggerTimers() {
      if (this.showing) return;
      if (this._pageViewDelayTimer) clearTimeout(this._pageViewDelayTimer);
      this._pageViewDelayTimer = null;
      this._cleanupPageViewScrollListener();
      this._pageViewTriggered = false;
      this._conditionPageViewLogic();
    }

    _disarmConditionTriggers() {
      if (this._pageViewDelayTimer) clearTimeout(this._pageViewDelayTimer);
      this._pageViewDelayTimer = null;
      this._cleanupPageViewScrollListener();
      this._pageViewTriggered = true;
    }

    canShowMini() {
      const content = (this.dataset.dlMiniContent || '').trim();
      if (!content) return false;
      if (this.check_show_page()) return false;
      if (this.hasFinished()) return false;
      if (!this.hasDrawn() && !this.check_only_unsubscribed()) return false;
      if (!localStorage.getItem(this.getStorageKey('opened'))) return false;
      if (!this.getCanShow(true)) return false;
      if (document.querySelector('.dl-pop-h-overlay.dl-show, dl-pop-raffle-y[data-showing="true"]')) return false;
      return true;
    }

    showMini() {
      window.DLPopMiniBar?.refresh?.();
    }

    hideMini() {
      if (typeof window.DLPopMiniBar?.hideWhilePopupShowing === 'function') {
        window.DLPopMiniBar.hideWhilePopupShowing();
        return;
      }
      window.DLPopMiniBar?.hide?.();
    }

    miniScroll() {
      window.DLPopMiniBar?.refresh?.();
    }

    cleanup() {
      cancelAnimationFrame(this.raf);
      this.stopCompositorIdleAnimation();
      this.timers.forEach(clearTimeout);
      this.cancelScheduledReset();
      if (this._pageViewDelayTimer) clearTimeout(this._pageViewDelayTimer);
      this._pageViewDelayTimer = null;
      this._cleanupPageViewScrollListener();
      window.removeEventListener('resize', this.onResize);
      document.removeEventListener('raffle:show', this.onShowRequest);
      document.removeEventListener('raffle:hide', this.onHideRequest);
      document.removeEventListener('click', this.onTriggerClick);
      window.DLPopMiniBar?.unregister?.(this.getMiniSourceId());
      this.overlay?.classList.remove('dl-show', 'dl-leaving');
      document.body.classList.remove('dl-raffle-y-open');
      this.dataset.showing = 'false';
      this.showing = false;
      this.unlockBodyScroll();
    }

    reset() {
      this.drawRequest += 1;
      this.winner = null;
      this.timers.forEach(clearTimeout);
      this.timers = [];
      this.querySelectorAll('form').forEach((form) => { form.reset(); form.classList.remove('has-error'); });
      this.querySelector('[data-raffle-confetti]').replaceChildren();
      this.setPhase('idle');
    }

    fit() {
      const wasMobile = this.mobile;
      this.mobile = matchMedia('(max-width: 990px)').matches;
      if (this.phase === 'idle' && this._idleCssActive) {
        if (wasMobile !== this.mobile) this.animateIdle();
        return;
      }
      this.renderCards();
    }

    setPhase(phase) {
      cancelAnimationFrame(this.raf);
      this.stopIdleAnimation();
      this.phase = phase;
      this.dataset.phase = phase;
      const panelPhase = phase === 'idle'
        ? 'entry'
        : ['draw', 'reveal', 'done'].includes(phase)
          ? 'reveal'
          : phase;
      this.querySelectorAll('[data-panel]').forEach((panel) => {
        const active = panel.dataset.panel === panelPhase;
        panel.classList.toggle('is-active', active);
        panel.setAttribute('aria-hidden', String(!active));
      });
      this.renderCards();
      if (phase === 'idle' && this.showing) this.animateIdle();
      if (phase === 'shuffle') this.animateShuffle();
    }

    isIOSWebKit() {
      return /iP(hone|ad|od)/.test(navigator.userAgent)
        || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    }

    animateIdle() {
      if (this.isIOSWebKit()) {
        this.animateIdleWithCss();
        return;
      }
      let previous = performance.now();
      const frame = (now) => {
        if (this.phase !== 'idle') return;
        this.tick += (now - previous) / 1000;
        previous = now;
        this.renderCards();
        this.raf = requestAnimationFrame(frame);
      };
      this.raf = requestAnimationFrame(frame);
    }

    animateIdleWithCss() {
      this.stopIdleAnimation();
      const count = this.cards.length || 1;
      const beat = 1.5;
      const duration = beat * count;
      const name = `dl-raffle-y-idle-${this.dataset.sectionId || this.id}`;
      if (!this._idleStyle) {
        this._idleStyle = document.createElement('style');
        this.appendChild(this._idleStyle);
      }
      this._idleStyle.textContent = this.buildIdleOrbitKeyframes(name);
      this.cards.forEach((card, i) => {
        const lag = .17 * (1 - Math.cos(2 * Math.PI * i / count));
        card.style.animation = `${name} ${duration}s linear infinite`;
        card.style.animationDelay = `${-(i * beat) + lag}s`;
        card.style.animationFillMode = 'both';
        card.style.willChange = 'transform, opacity';
      });
      this._idleCssActive = true;
      this.startIdleLayerUpdates();
    }

    buildIdleOrbitKeyframes(name) {
      const base = this.mobile ? 375 : 1920;
      const l = this.mobile
        ? { w:327, arc:519, pivot:679, scale:.7402 }
        : { w:800, arc:701, pivot:907, scale:1 };
      const count = this.cards.length || 1;
      const beat = 1.5;
      const duration = beat * count;
      const sampleCount = count * 24;
      const vw = (value) => `${(value / base) * 100}vw`;
      const at = (x, y, r) => `translate3d(${vw(x - 102)}, ${vw(y - 142)}, 0) rotate(${r}deg) scale(${l.scale})`;
      const lines = [];
      let prevTheta = null;
      let prevTransform = null;
      for (let sample = 0; sample <= sampleCount; sample += 1) {
        const offset = sample / sampleCount;
        const theta = this.wrapIdleTheta(this.advance(duration * offset));
        const rad = theta * Math.PI / 180;
        const opacity = this.idleCardOpacity(theta);
        const transform = at(
          l.w / 2 + l.arc * Math.sin(rad),
          l.pivot - l.arc * Math.cos(rad),
          theta
        );
        const wrapped = prevTheta != null && Math.abs(theta - prevTheta) > 90;
        if (wrapped && prevTransform) {
          const hidePrev = Math.max(0, offset - 0.0002);
          const showNext = Math.min(1, offset + 0.0002);
          lines.push(`${(hidePrev * 100).toFixed(4)}%{transform:${prevTransform};opacity:0}`);
          lines.push(`${(offset * 100).toFixed(4)}%{transform:${transform};opacity:0}`);
          lines.push(`${(showNext * 100).toFixed(4)}%{transform:${transform};opacity:${opacity}}`);
        } else {
          lines.push(`${(offset * 100).toFixed(4)}%{transform:${transform};opacity:${opacity}}`);
        }
        prevTheta = theta;
        prevTransform = transform;
      }
      return `@keyframes ${name}{${lines.join('')}}`;
    }

    startIdleLayerUpdates() {
      if (this._idleZTimer) clearTimeout(this._idleZTimer);
      const count = this.cards.length || 1;
      const spacing = 120 / count;
      const beat = 1.5;
      const startedAt = performance.now();
      const update = () => {
        if (this.phase !== 'idle' || !this._idleCssActive) return;
        const t = (performance.now() - startedAt) / 1000;
        this.cards.forEach((card, i) => {
          const lag = .17 * (1 - Math.cos(2 * Math.PI * i / count));
          const theta = this.wrapIdleTheta(i * spacing + this.advance(t - lag));
          card.style.zIndex = String(Math.round((theta + 60) * 10));
        });
        this._idleZTimer = setTimeout(update, 80);
      };
      update();
    }

    stopIdleAnimation() {
      this.stopCompositorIdleAnimation();
    }

    stopCompositorIdleAnimation() {
      if (this._idleZTimer) {
        clearTimeout(this._idleZTimer);
        this._idleZTimer = null;
      }
      if (this._idleCssActive) {
        this.cards.forEach((card) => {
          card.style.animation = '';
          card.style.animationDelay = '';
          card.style.animationFillMode = '';
          card.style.willChange = '';
        });
        this._idleCssActive = false;
      }
      if (!this.idleAnimations?.length) return;
      this.idleAnimations.forEach((animation) => animation.cancel());
      this.idleAnimations = [];
      this.idleAnimationStartedAt = 0;
    }

    // 等待抽奖接口时持续切牌：堆叠后快速轮换顶牌
    animateShuffle() {
      this.shuffleTick = 0;
      this.shuffleIndex = 0;
      let previous = performance.now();
      const frame = (now) => {
        if (this.phase !== 'shuffle') return;
        this.shuffleTick += (now - previous) / 1000;
        previous = now;
        const count = this.cards.length || 1;
        this.shuffleIndex = Math.floor(this.shuffleTick / 0.34) % count;
        this.renderCards();
        this.raf = requestAnimationFrame(frame);
      };
      this.raf = requestAnimationFrame(frame);
    }

    advance(seconds) {
      const beat = 1.5;
      const step = 120 / this.cards.length;
      const k = Math.floor(seconds / beat);
      const local = seconds - k * beat;
      const raw = Math.min(1, local / .85);
      const eased = raw < .5 ? 2 * raw * raw : 1 - Math.pow(-2 * raw + 2, 2) / 2;
      return (k + eased) * step;
    }

    wrapIdleTheta(theta) {
      return ((theta + 60) % 120 + 120) % 120 - 60;
    }

    idleCardOpacity(theta) {
      if (theta > 40) return 0;
      const abs = Math.abs(theta);
      return 1 - Math.min(1, Math.max(0, (abs - 40) / 10));
    }

    renderCards() {
      if (!this.cards?.length) return;
      const base = this.mobile ? 375 : 1920;
      const l = this.mobile
        ? { w:327, arc:519, pivot:679, scale:.7402, stack:222, hero:252, heroScale:1.2, pull:118, more:218.4 }
        : { w:800, arc:701, pivot:907, scale:1, stack:250, hero:301, heroScale:1.5, pull:220, more:285 };
      const count = this.cards.length;
      const spacing = 120 / count;
      const top = count - 1;
      const unit = (value) => `${(value / base) * 100}vw`;
      const at = (x, y, r, s) => `translate3d(${unit(x - 102)}, ${unit(y - 142)}, 0) rotate(${r}deg) scale(${s})`;
      const idle = this.phase === 'idle';
      this.cards.forEach((card, i) => {
        let transform, opacity = 1, z = i, flip = 0;
        if (idle) {
          const lag = .17 * (1 - Math.cos(2 * Math.PI * i / count));
          const theta = this.wrapIdleTheta(i * spacing + this.advance(this.tick - lag));
          const rad = theta * Math.PI / 180;
          transform = at(l.w/2 + l.arc*Math.sin(rad), l.pivot-l.arc*Math.cos(rad), theta, l.scale);
          opacity = this.idleCardOpacity(theta);
          z = Math.round((theta+60)*10);
        } else if (this.phase === 'gather') {
          transform = at(l.w/2,l.stack,0,l.scale);
        } else if (this.phase === 'flip') {
          transform = at(l.w/2,l.stack,0,l.scale); flip = 180; opacity = i === top ? 1 : 0;
        } else if (this.phase === 'shuffle') {
          const active = ((this.shuffleIndex % count) + count) % count;
          const offset = i === active ? 0 : ((i - active + count) % count);
          const shuffleDistance = l.pull * 0.35;
          const shuffleStackX = l.w / 2 - shuffleDistance / 2;
          flip = 180;
          if (i === active) {
            transform = at(shuffleStackX + shuffleDistance, l.stack, 0, l.scale);
            z = 220;
          } else if (offset === 1) {
            transform = at(shuffleStackX, l.stack, 0, l.scale);
            z = 200;
            opacity = 1;
          } else {
            transform = at(shuffleStackX, l.stack, 0, l.scale);
            opacity = 0;
            z = 100;
          }
        } else if (this.phase === 'pull') {
          flip = 180;
          if (i === this.winner) { transform = at(l.w/2+l.pull,l.stack,0,l.scale); z=50; }
          else if (i === top) { transform = at(l.w/2,l.stack,0,l.scale); z=200; }
          else { transform = at(l.w/2,l.stack,0,l.scale); opacity=0; }
        } else if (['draw','reveal','done','more','check'].includes(this.phase)) {
          if (i === this.winner) {
            const isMore = this.phase === 'more';
            transform = at(l.w/2,isMore?l.more:l.hero,0,l.heroScale);
            flip = ['reveal','done','more','check'].includes(this.phase) ? 360 : 180;
            opacity = this.phase === 'check' ? 0 : 1; z=300;
          } else { transform=at(l.w/2,l.stack,0,l.scale*.94); flip=180; opacity=0; z=100; }
        }
        if (card.style.transform !== transform) card.style.transform = transform;
        if (card.style.opacity !== String(opacity)) card.style.opacity = String(opacity);
        if (!idle) {
          const visibility = opacity <= 0 ? 'hidden' : 'visible';
          if (card.style.visibility !== visibility) card.style.visibility = visibility;
        } else if (card.style.visibility !== 'visible') {
          card.style.visibility = 'visible';
        }
        const zStr = String(z);
        if (card.style.zIndex !== zStr) card.style.zIndex = zStr;
        const flipper = this.cardFlippers?.[i];
        const flipTransform = `rotateY(${flip}deg)`;
        if (flipper && flipper.style.transform !== flipTransform) flipper.style.transform = flipTransform;
        const shouldDisable = !(this.phase === 'draw' && i === this.winner);
        if (card.disabled !== shouldDisable) card.disabled = shouldDisable;
      });
    }

    createRequestId() {
      if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID();
      }
      return `raffle-${Date.now()}-${Math.random().toString(16).slice(2)}`;
    }

    resolvePrize(result) {
      const prizeId = String(result?.id ?? '').trim();
      const winner = this.cards.findIndex((card) => card.dataset.prizeId === prizeId);
      if (!prizeId || winner < 0) throw new Error(`Unknown raffle prize id: ${prizeId || '(empty)'}`);
      return { prizeId, winner, result };
    }

    formatPhoneE164(phone) {
      const countryCode = String(this.dataset.dlCountryCode || '+1').trim() || '+1';
      const digits = String(phone || '').replace(/\D/g, '');
      if (!digits) return '';
      if (String(phone || '').trim().startsWith('+')) return `+${digits}`;
      const codeDigits = countryCode.replace(/\D/g, '') || '1';
      const local = digits.startsWith(codeDigits) ? digits.slice(codeDigits.length) : digits;
      return `+${codeDigits}${local}`;
    }

    async requestDraw(payload) {
      const url = this.dataset.raffleApiUrl?.trim() || '/apps/petlibro-api/raffles/raffle-popup-y/draw';
      const body = {
        requestId: this.createRequestId(),
        popupId: this.id || 'raffle-popup-y',
        sectionId: this.dataset.sectionId || null,
        ...payload
      };
      console.log('[DLPopRaffleY] Raffle request:', body);

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      });

      const responseText = await response.text();
      console.log('[DLPopRaffleY] Raffle status:', response.status);
      console.log('[DLPopRaffleY] Raffle response:', responseText);

      let result;
      try {
        result = JSON.parse(responseText);
      } catch (_) {
        throw new Error(`Invalid server response (${response.status})`);
      }

      if (!response.ok) {
        const error = new Error(result.error?.message || `Raffle failed (${response.status})`);
        error.code = result.error?.code;
        error.details = result.error?.details;
        throw error;
      }

      return result;
    }

    async requestPrize(email) {
      const result = await this.requestDraw({
        channel: 'email',
        email: String(email || '').trim()
      });
      return this.resolvePrize(result);
    }

    async requestSms(phone) {
      const email = String(this.submittedEmail || this.getDrawn()?.email || '').trim();
      if (!email) throw new Error('Missing email from raffle entry');
      return this.requestDraw({
        channel: 'sms',
        email,
        phone: String(phone || '').trim()
      });
    }

    waitForDrawAnimation(delay) {
      return new Promise((resolve) => {
        this.timers.push(setTimeout(resolve, delay));
      });
    }

    async start(event) {
      event.preventDefault();
      event.stopPropagation();
      if (this.phase !== 'idle') return;
      const form = this.querySelector('.dl-raffle-y__form');
      if (!form) return;
      const email = form.elements.email;
      const error = form.querySelector('[data-email-error]');
      if (error) error.textContent = error.dataset.validationMessage;
      if (!email.checkValidity()) { form.classList.add('has-error'); email.focus(); return; }
      form.classList.remove('has-error');
      const emailValue = email.value.trim();
      this.submittedEmail = emailValue;
      const requestId = ++this.drawRequest;
      const detail = { email: emailValue, sectionId: this.dataset.sectionId };
      this.dispatchEvent(new CustomEvent('raffle:email', { bubbles: true, detail }));
      // 订阅由后端抽奖接口统一处理，前端只做埋点，避免重复订阅/重复抽奖。
      this.capturePixel('pop_up_submit', {
        [`pop_prop_pop-${this.event_pop_id}-email`]: emailValue,
        shopify_store: this.getShopifyStore()
      });
      this.setPhase('gather');
      this.timers.push(setTimeout(() => {
        if (requestId === this.drawRequest) this.setPhase('shuffle');
      }, 700));

      try {
        const startedAt = Date.now();
        const { prizeId, winner } = await this.requestPrize(emailValue);
        // 接口很快时至少切一会儿牌，避免瞬间跳到揭晓
        const elapsed = Date.now() - startedAt;
        if (elapsed < 1400) await this.waitForDrawAnimation(1400 - elapsed);
        if (requestId !== this.drawRequest) return;
        this.markDrawn(prizeId, winner, emailValue);
        this.winner = winner;
        this.dispatchEvent(new CustomEvent('raffle:result', {
          bubbles: true,
          detail: { prizeId, index: winner, sectionId: this.dataset.sectionId }
        }));
        this.setPhase('pull');
        this.timers.push(setTimeout(() => {
          if (requestId === this.drawRequest) this.setPhase('draw');
        }, 550));
      } catch (requestError) {
        if (requestId !== this.drawRequest) return;
        console.error('[DLPopRaffleY] Prize request failed', requestError);
        this.timers.forEach(clearTimeout);
        this.timers = [];
        this.winner = null;
        this.setPhase('idle');
        if (error) error.textContent = requestError.message || this.dataset.raffleApiError || error.dataset.validationMessage;
        form.classList.add('has-error');
      }
    }

    reveal() {
      if (this.phase !== 'draw') return;
      this.markRevealed();
      this.setPhase('reveal');
      const card = this.cards[this.winner];
      this.dispatchEvent(new CustomEvent('raffle:won', { bubbles:true, detail:{ prizeId:card.dataset.prizeId, index:this.winner, sectionId:this.dataset.sectionId } }));
      this.confetti();
      this.timers.push(setTimeout(()=>this.setPhase('done'),500),setTimeout(()=>this.setPhase('more'),1700));
    }

    confetti() {
      const box = this.querySelector('[data-raffle-confetti]');
      if (!box) return;
      box.replaceChildren();
      const colors = ['#f57474','#67cbab','#f5c574','#315800'];
      const { width, height } = box.getBoundingClientRect();
      const pieceCount = this.mobile ? 48 : 64;
      let longestAnimation = 0;

      for (let i=0;i<pieceCount;i++) {
        const piece=document.createElement('i');
        const delay = Math.pow(Math.random(), 1.4) * 1.15;
        const duration = 2.4 + Math.random() * 1.4;
        const fallDistance = height + 60 + Math.random() * Math.max(60, height * .15);
        const horizontalTravel = (Math.random() - .5) * Math.max(220, width * .72);
        const sway = (Math.random() - .5) * Math.max(50, width * .18);
        longestAnimation = Math.max(longestAnimation, delay + duration);

        piece.style.left=`${4+Math.random()*92}%`;
        piece.style.setProperty('--color',colors[Math.floor(Math.random()*colors.length)]);
        piece.style.setProperty('--fall',`${fallDistance}px`);
        piece.style.setProperty('--tx',`${horizontalTravel}px`);
        piece.style.setProperty('--sway',`${sway}px`);
        piece.style.setProperty('--rot',`${(Math.random()-.5)*1080}deg`);
        piece.style.setProperty('--delay',`${delay}s`);
        piece.style.setProperty('--dur',`${duration}s`);
        piece.style.setProperty('--w',`${6+Math.random()*7}px`);
        piece.style.setProperty('--h',`${9+Math.random()*12}px`);
        piece.style.setProperty('--round',Math.random()>.6?'50%':'2px');
        piece.append(document.createElement('span'));
        box.append(piece);
      }
      this.timers.push(setTimeout(()=>box.replaceChildren(),(longestAnimation + .2) * 1000));
    }

    async submitPhone(event) {
      event.preventDefault();
      if (this.phase !== 'more' || this._phoneSubmitting) return;
      const form = event.currentTarget;
      const phone = form.elements.phone;
      const error = form.querySelector('[data-phone-error]');
      if (error) error.textContent = error.dataset.validationMessage || error.textContent;
      if (!phone.value.trim()) { form.classList.add('has-error'); phone.focus(); return; }

      const phoneValue = this.formatPhoneE164(phone.value);
      if (!phoneValue || phoneValue.length < 8) {
        form.classList.add('has-error');
        phone.focus();
        return;
      }

      form.classList.remove('has-error');
      const emailValue = String(this.submittedEmail || this.getDrawn()?.email || '').trim();
      if (!emailValue) {
        if (error) error.textContent = this.dataset.raffleApiError || error.dataset.validationMessage;
        form.classList.add('has-error');
        return;
      }

      const prizeId = this.cards[this.winner]?.dataset.prizeId;
      const submitButton = form.querySelector('[type="submit"]');
      this._phoneSubmitting = true;
      if (submitButton) {
        submitButton.disabled = true;
        submitButton.classList.add('is-loading');
        submitButton.setAttribute('aria-busy', 'true');
      }

      this.dispatchEvent(new CustomEvent('raffle:phone', {
        bubbles: true,
        detail: { email: emailValue, phone: phoneValue, prizeId, sectionId: this.dataset.sectionId }
      }));
      this.capturePixel('pop_up_phone_subscribe', {
        [`pop_prop_pop-${this.event_pop_id}-email`]: emailValue,
        [`pop_prop_pop-${this.event_pop_id}-phone`]: phoneValue,
        prize_id: prizeId,
        shopify_store: this.getShopifyStore()
      });

      try {
        const result = await this.requestSms(phoneValue);
        if (this.phase !== 'more') return;
        this.dispatchEvent(new CustomEvent('raffle:phone-result', {
          bubbles: true,
          detail: { email: emailValue, phone: phoneValue, prizeId, result, sectionId: this.dataset.sectionId }
        }));
        this.finish();
        this.setPhase('check');
      } catch (requestError) {
        if (this.phase !== 'more') return;
        console.error('[DLPopRaffleY] SMS request failed', requestError);
        if (error) {
          error.textContent = requestError.message || this.dataset.raffleApiError || error.dataset.validationMessage;
        }
        form.classList.add('has-error');
      } finally {
        this._phoneSubmitting = false;
        if (submitButton) {
          submitButton.disabled = false;
          submitButton.classList.remove('is-loading');
          submitButton.removeAttribute('aria-busy');
        }
      }
    }
  }

  customElements.define('dl-pop-raffle-y', DLPopRaffleY);
}
